import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/context/AuthContext";
import { Mail, Send, Trash2, Users, History, Eye, Loader2 } from "lucide-react";
import { Field } from "../shared.jsx";

/**
 * NewsletterManager
 * -----------------
 * Studio tab that lets the site owner:
 *   1. Compose the monthly newsletter (subject + plain-text body)
 *   2. Send a preview only to themselves
 *   3. Send to every subscriber (with confirm)
 *   4. See past sends (subject, recipient count, timestamp)
 *   5. View the current subscriber list + delete rows (spam / typos)
 */
export default function NewsletterManager() {
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [subscribers, setSubscribers] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(""); // '' | 'preview' | 'send'

    async function load() {
        setLoading(true);
        try {
            const [s, h] = await Promise.all([
                api.get("/admin/newsletter/subscribers"),
                api.get("/admin/newsletter/history"),
            ]);
            setSubscribers(s.data || []);
            setHistory(h.data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);

    async function preview() {
        if (!subject.trim() || body.trim().length < 10) {
            toast.error("Give it a subject and at least a sentence of body.");
            return;
        }
        setBusy("preview");
        try {
            const r = await api.post("/admin/newsletter/preview", { subject, body });
            const where = r.data?.sent_to || "your admin email";
            if (r.data?.resend_enabled) {
                toast.success(`Preview sent to ${where}. Check your inbox.`);
            } else {
                toast.info(`Preview generated (email backend disabled locally — would send to ${where}).`);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(""); }
    }

    async function sendAll() {
        if (!subject.trim() || body.trim().length < 10) {
            toast.error("Give it a subject and at least a sentence of body.");
            return;
        }
        const count = subscribers.length;
        if (count === 0) {
            toast.error("No subscribers on the list yet.");
            return;
        }
        if (!window.confirm(`Send "${subject}" to all ${count} subscriber${count === 1 ? "" : "s"}? This cannot be undone.`)) return;
        setBusy("send");
        try {
            const r = await api.post("/admin/newsletter/send", { subject, body });
            toast.success(`Newsletter sent to ${r.data?.sent_to_count || 0} subscribers${r.data?.failed_count ? ` · ${r.data.failed_count} failed` : ""}.`);
            setSubject("");
            setBody("");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(""); }
    }

    async function removeSubscriber(email) {
        if (!window.confirm(`Remove ${email} from the newsletter?`)) return;
        try {
            await api.delete(`/admin/newsletter/subscribers/${encodeURIComponent(email)}`);
            setSubscribers((list) => list.filter((s) => s.email !== email));
            toast.success("Removed.");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-testid="newsletter-manager">
            {/* Compose */}
            <div className="lg:col-span-2 space-y-6">
                <div className="flex items-center gap-2">
                    <Mail size={16} className="text-[var(--sb-brand)]" />
                    <h2 className="uppercase tracking-[0.28em] text-xs text-[var(--sb-text-dim)]">Compose Monthly Dispatch</h2>
                </div>
                <Field label="Subject line" full>
                    <input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        maxLength={180}
                        placeholder="August dispatch — Kinnoull Hill, Sma Glen, and one honest confession about weather apps."
                        className="w-full bg-transparent border border-[var(--sb-border)] px-3 py-2 focus:outline-none focus:border-[var(--sb-brand)] text-[var(--sb-text)]"
                        data-testid="newsletter-subject"
                    />
                </Field>
                <Field label={`Body (${body.length}/20000 · paragraphs separated by empty lines)`} full>
                    <textarea
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        rows={14}
                        maxLength={20000}
                        placeholder={"Big news this month — Harry is back in the air after his service in Nottingham.\n\nThis week's flight took me over Kinnoull Hill, the vantage point above Perth where the River Tay curves…\n\n(Empty lines create new paragraphs.)"}
                        className="w-full bg-transparent border border-[var(--sb-border)] px-3 py-3 focus:outline-none focus:border-[var(--sb-brand)] text-[var(--sb-text)] font-mono text-sm leading-relaxed"
                        data-testid="newsletter-body"
                    />
                </Field>

                <div className="flex flex-wrap gap-3 pt-2">
                    <button
                        onClick={preview}
                        disabled={busy !== ""}
                        className="btn-outline inline-flex items-center gap-2"
                        data-testid="newsletter-preview-btn"
                    >
                        {busy === "preview" ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                        Send preview to me
                    </button>
                    <button
                        onClick={sendAll}
                        disabled={busy !== "" || subscribers.length === 0}
                        className="btn-primary inline-flex items-center gap-2"
                        data-testid="newsletter-send-btn"
                    >
                        {busy === "send" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                        Send to {subscribers.length} subscriber{subscribers.length === 1 ? "" : "s"}
                    </button>
                </div>

                {/* History */}
                <div className="pt-8 border-t border-[var(--sb-border)]">
                    <div className="flex items-center gap-2 mb-4">
                        <History size={16} className="text-[var(--sb-text-dim)]" />
                        <h3 className="uppercase tracking-[0.28em] text-xs text-[var(--sb-text-dim)]">Send history</h3>
                    </div>
                    {loading ? (
                        <p className="text-sm text-[var(--sb-text-muted)]">Loading…</p>
                    ) : history.length === 0 ? (
                        <p className="text-sm text-[var(--sb-text-muted)] italic">No newsletters sent yet.</p>
                    ) : (
                        <div className="space-y-2" data-testid="newsletter-history">
                            {history.map((h) => (
                                <div key={h.id} className="border border-[var(--sb-border)] p-3 flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="text-sm text-[var(--sb-text)] truncate">{h.subject}</div>
                                        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sb-text-muted)] mt-1">
                                            {h.sent_at?.slice(0, 16).replace("T", " ")} · sent to {h.sent_to_count}
                                            {h.failed_count > 0 && <span className="text-amber-400"> · {h.failed_count} failed</span>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Subscribers */}
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <Users size={16} className="text-[var(--sb-brand)]" />
                    <h2 className="uppercase tracking-[0.28em] text-xs text-[var(--sb-text-dim)]">Subscribers ({subscribers.length})</h2>
                </div>
                {loading ? (
                    <p className="text-sm text-[var(--sb-text-muted)]">Loading…</p>
                ) : subscribers.length === 0 ? (
                    <p className="text-sm text-[var(--sb-text-muted)] italic">Nobody has signed up yet.</p>
                ) : (
                    <div className="space-y-1 max-h-[600px] overflow-y-auto pr-1" data-testid="newsletter-subscribers">
                        {subscribers.map((s) => (
                            <div
                                key={s.email}
                                className="group flex items-center justify-between gap-2 px-3 py-2 border border-[var(--sb-border)] hover:border-[var(--sb-brand)] transition-colors"
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm text-[var(--sb-text)] truncate">{s.email}</div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--sb-text-muted)]">
                                        {s.subscribed_at?.slice(0, 10)}
                                    </div>
                                </div>
                                <button
                                    onClick={() => removeSubscriber(s.email)}
                                    className="opacity-40 group-hover:opacity-100 transition-opacity text-[var(--sb-text-muted)] hover:text-red-400"
                                    title="Remove from list"
                                    data-testid={`newsletter-remove-${s.email}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
